import React from 'react';
import { AlertTriangle } from 'react-feather';

interface SelectOptionComponentProps {
  options: string[] | number[];
  value: string;
  compareColumn?: string;
  lastUpdateDate?: string | null;
  onChange: (newValue: string) => void;
}

// Calculate days since last update for stale indicator
const getDaysSinceUpdate = (dateString: string | null | undefined): number | null => {
  if (!dateString) return null
  const lastUpdate = new Date(dateString)
  const now = new Date()
  const diffTime = now.getTime() - lastUpdate.getTime()
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24))
  return diffDays
}

const SelectOptionComponent: React.FC<SelectOptionComponentProps> = ({ options, compareColumn, value, onChange, lastUpdateDate }) => {
  const isYellowStripe = value === 'Yellow Stripe'
  const daysSinceUpdate = isYellowStripe ? getDaysSinceUpdate(lastUpdateDate) : null

  // Determine urgency styling
  const getUrgencyClass = () => {
    if (!isYellowStripe || daysSinceUpdate === null) return ''
    if (daysSinceUpdate >= 7) return 'ring-2 ring-red-500'
    if (daysSinceUpdate >= 4) return 'ring-2 ring-orange-500'
    return ''
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <div className="flex items-center gap-1.5">
        {isYellowStripe && <AlertTriangle size={14} className="text-yellow-400" />}
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`
            px-3 py-2 min-w-32 text-sm font-medium rounded-lg
            transition-all duration-200 ease-in-out
            cursor-pointer appearance-none
            bg-no-repeat bg-[length:16px_16px] bg-[right_8px_center]
            ${isYellowStripe
              ? 'bg-yellow-600/20 text-yellow-400 border border-yellow-500/50 hover:border-yellow-400'
              : value === 'Pending'
                ? 'bg-red-500/10 text-red-400 border border-red-500/50 hover:border-red-400'
                : 'bg-dark-card text-gray-200 border border-dark-border hover:border-gray-500 hover:bg-dark-card-hover'
            }
            focus:outline-none focus:ring-2 focus:ring-accent-teal/50 focus:border-accent-teal
            ${getUrgencyClass()}
          `}
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            paddingRight: '32px'
          }}
        >
          {options.map((option, index) => (
            <option key={index} value={option} className="bg-dark-card text-gray-100">
              {option}
            </option>
          ))}
        </select>
      </div>
      {isYellowStripe && daysSinceUpdate !== null && (
        <span className={`text-[10px] font-medium ml-1 ${
          daysSinceUpdate >= 7 ? 'text-red-400' :
          daysSinceUpdate >= 4 ? 'text-orange-400' :
          'text-yellow-400'
        }`}>
          {daysSinceUpdate === 0 ? 'Stale today' : daysSinceUpdate === 1 ? '1 day stale' : `${daysSinceUpdate} days stale`}
        </span>
      )}
    </div>
  );
};

export default SelectOptionComponent;